import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { BookOpen, Image as ImageIcon, LayoutDashboard, Inbox, Tags, GraduationCap, MessagesSquare, Megaphone, Settings, LogOut, ExternalLink, Calculator } from "lucide-react";
import { http, setToken, getToken } from "@/lib/api";

const ITEMS = [
  ["", "Oversikt", LayoutDashboard],
  ["henvendelser", "Henvendelser", Inbox],
  ["priser", "Priser", Tags],
  ["sykurs", "Sykurs", GraduationCap],
  ["handbok", "Håndbok", BookOpen],
  ["bilder", "Bilder", ImageIcon],
  ["samtaler", "AI-samtaler", MessagesSquare],
  ["markedsforing", "AI Markedsføring", Megaphone],
  ["regnskap", "Regnskap", Calculator],
  ["innstillinger", "Innstillinger", Settings],
];

export const AdminTitle = ({ title, sub, children }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-6 border-b border-[#E5E0D8]">
    <div>
      <h1 className="text-4xl font-medium tracking-tight">{title}</h1>
      {sub && <p className="text-sm text-[#5C5A56] mt-2 max-w-2xl">{sub}</p>}
    </div>
    {children}
  </div>
);

export default function AdminLayout() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => { setToken(null); nav("/admin/login"); });
  }, [nav]);

  if (!user) return <div className="p-10 text-sm" data-testid="admin-loading">Laster…</div>;

  const logout = () => { setToken(null); nav("/admin/login"); };
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#FDFBF7]" data-testid="admin-layout">
      <aside className="md:w-64 md:min-h-screen bg-[#1A1A1A] text-[#E5E0D8] flex md:flex-col">
        <div className="hidden md:block p-6 border-b border-white/10">
          <p className="font-serif-display text-2xl text-[#FDFBF7]">SømSignature</p>
          <p className="text-[0.6rem] tracking-[0.3em] uppercase text-[#C4A47C] mt-1">Admin · {user.name}</p>
        </div>
        <nav className="flex md:flex-col overflow-x-auto md:overflow-visible flex-1 py-2">
          {ITEMS.map(([to, label, Icon]) => (
            <NavLink key={to} to={`/admin${to ? `/${to}` : ""}`} end={!to}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm whitespace-nowrap transition-colors ${isActive ? "bg-white/10 text-[#C4A47C]" : "hover:text-[#C4A47C]"}`}
              data-testid={`admin-nav-${to || "overview"}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} />{label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex flex-col border-t border-white/10 p-4 gap-2 text-sm">
          <Link to="/" className="flex items-center gap-2 px-2 py-2 hover:text-[#C4A47C]" data-testid="admin-view-site"><ExternalLink className="w-4 h-4" strokeWidth={1.5} />Se nettsiden</Link>
          <button onClick={logout} className="flex items-center gap-2 px-2 py-2 hover:text-[#C4A47C]" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" strokeWidth={1.5} />Logg ut</button>
        </div>
      </aside>
      <main className="flex-1 p-6 md:p-10 overflow-x-hidden"><Outlet /></main>
    </div>
  );
}
